import { createContext, useContext, type ReactNode } from "react";
import { Link } from "wouter";
import { icons } from "./icons";

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(" ");

/** The page container: 1440px by default (the mockup's), `xl` 1280 or `lg` 1024 for narrower pages. */
export function PageShell({ maxWidth = "2xl", children }: { maxWidth?: "lg" | "xl" | "2xl"; children: ReactNode }) {
  return <div className={cx("pageshell", maxWidth !== "2xl" && `pageshell--${maxWidth}`)}>{children}</div>;
}

/**
 * Opens help for the current page (ALN-53). The app shell provides it; PageHeader then shows a Help
 * button first among the actions, as the mockup does on every page.
 */
export const PageHelpContext = createContext<(() => void) | null>(null);

/** Page title (the only h1), subtitle, breadcrumbs and actions. `serif` is for record titles. */
export function PageHeader({ title, subtitle, breadcrumbs, actions, serif }: { title: ReactNode; subtitle?: ReactNode; breadcrumbs?: { label: ReactNode; href?: string }[]; actions?: ReactNode; serif?: boolean }) {
  const openHelp = useContext(PageHelpContext);
  const help = openHelp && (
    <button type="button" className="btn btn--ghost helpbtn" title="Help for this page (?)" onClick={openHelp}>
      {icons.help}<span>Help<span className="sr"> for this page</span></span>
    </button>
  );
  return (
    <div className="pagehead">
      <div className="pagehead__main">
        {breadcrumbs && (
          <nav className="crumbs" aria-label="Breadcrumb">
            {breadcrumbs.map((b, i) => (
              <span key={i}>{b.href ? <Link href={b.href}>{b.label}</Link> : b.label}{i < breadcrumbs.length - 1 ? " ›" : ""}</span>
            ))}
          </nav>
        )}
        <h1 className={cx("pagetitle", serif && "pagetitle--serif")}>{title}</h1>
        {subtitle && <p className="pagesub">{subtitle}</p>}
      </div>
      {(actions || help) && <div className="actions">{help}{actions}</div>}
    </div>
  );
}

/** A card with an optional heading row (title, description, right-hand actions). */
export function Card({ title, description, actions, headingId, children, className }: { title?: ReactNode; description?: ReactNode; actions?: ReactNode; headingId?: string; children?: ReactNode; className?: string }) {
  return (
    <section className={cx("card", className)} aria-labelledby={title ? headingId : undefined}>
      {(title || actions) && (
        <div className="card__h">
          <div>
            {title && <h2 className="card__t" id={headingId}>{title}</h2>}
            {description && <p className="card__d">{description}</p>}
          </div>
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

/** Summary tiles above a page's main table. */
export function KpiStrip({ items }: { items: { label: string; value: ReactNode; sub?: ReactNode }[] }) {
  return (
    <div className="tiles">
      {items.map(i => (
        <div className="card tile" key={i.label}>
          <div className="tile__k">{i.label}</div>
          <div className="tile__v">{i.value}</div>
          {i.sub && <div className="tile__s muted">{i.sub}</div>}
        </div>
      ))}
    </div>
  );
}
